import { tWithKeyAttr as t } from "../../../i18nHelper";
import changeVideoPopup from "./changeVideoPopup.module.css";
import React, { useState } from "react";
import { IPopupWithoutProps } from "../../../types/types";
import ChangePasswordSuccessPopup from "../changePasswordSuccessPopup/changePasswordSuccessPopup";
import { useFormik } from "formik";
import { changeVideoValidationSchema } from "../../../utils/validation";
import { getAllowedExternalVideoEmbedUrl } from "../../../utils/utils";
import loginStyles from "../../../pages/login/login.module.css";
import {
  useDeleteTherapistIntroVideoMutation,
  useLazyGetUserQuery,
  useUpdateTherapistIntroVideoLinkMutation,
  useUploadTherapistIntroVideoMutation,
} from "../../../services/api/user.api";
import { useAppSelector } from "../../../services/hooks";
import { errorUserSelector } from "../../../services/selectors/auth.selectors";
import { userSelector } from "../../../services/selectors/user.selectors";
import Modal from "../modal/Modal";
import ErrorPopup from "../errorPopup/errorPopup";
import uploadIcon from "../../../images/upload_icon.svg";

function ChangeVideoPopup({ onClose }: IPopupWithoutProps) {
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [openErrorModal, setOpenErrorModal] = useState<boolean>(false);
  const [video, setVideo] = useState<File | null>(null);
  const [videoURL, setVideoURL] = useState<string | null>(null);
  const [openDeleteSuccessPopup, setOpenDeleteSuccessPopup] = useState(false);

  const [deleteTherapistIntroVideo, { isError: isDeleteError }] = useDeleteTherapistIntroVideoMutation();
  const [updateTherapistIntroVideoLink, { isError: isLinkError }] = useUpdateTherapistIntroVideoLinkMutation();
  const [uploadVideo, { isError: isUploadError }] = useUploadTherapistIntroVideoMutation();
  const [getUser] = useLazyGetUserQuery();

  const MAX_FILE_SIZE = 30 * 1024 * 1024; // 30MB
  const user = useAppSelector(userSelector);
  const updateError = useAppSelector(errorUserSelector);
  const hasVideo = Boolean(user.video);

  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleSubmit = async () => {
    try {
      if (video) {
        // upload file
        const formData = new FormData();
        formData.append("video", video);
        const response = await uploadVideo({
          form: formData,
        });
        if ("data" in response && response.data) {
          setOpenModal(true);
          getUser();
        } else {
          setOpenErrorModal(true);
        }
      } else if (formik.values.link) {
        // update link
        const response = await updateTherapistIntroVideoLink({
          video: formik.values.link,
        });
        if ("data" in response && response.data) {
          setOpenModal(true);
          getUser();
        } else {
          setOpenErrorModal(true);
        }
      }
    } catch (error) {
      console.error(error);
      setOpenErrorModal(true);
    }
  };

const handleVideoChange = (event: React.ChangeEvent<HTMLInputElement>) => {
  const file = event.target.files?.[0];
  if (file) {
    if (file.size > MAX_FILE_SIZE) {
      setErrorMessage("הקובץ חורג מהמגבלה (30MB)");
      setVideo(null);
      setVideoURL(null);
      formik.setFieldValue("video", null);
      formik.setFieldValue("videoName", "");
      return;
    }

    setErrorMessage(null); 
    setVideo(file);
    setVideoURL(URL.createObjectURL(file));
    // Clear link if file is selected
    formik.setFieldValue("link", "");
    formik.setFieldValue("video", file);
    formik.setFieldValue("videoName", file.name);
  }
};

  const clearVideoFile = () => {
    setVideo(null);
    setVideoURL(null);
    formik.setFieldValue("video", "");
    formik.setFieldValue("videoName", "");
  };

  const clearVideoLink = () => {
    formik.setFieldValue("link", "");
  };

  const formik = useFormik({
    initialValues: {
      video: "",
      videoName: "",
      link: "",
    },
    validationSchema: changeVideoValidationSchema,
    onSubmit: () => {
      handleSubmit();
    },
  });

  const handleLinkChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    formik.setFieldValue("link", value);
    if (value) {
      // Clear file if link is entered
      clearVideoFile();
    }
  };

  const handleDeleteVideo = async () => {
    const confirmed = window.confirm(t("CHANGE_VIDEO_DELETE_CONFIRM"));
    if (!confirmed) return;
    try {
      const response = await deleteTherapistIntroVideo();

      if ("data" in response && response.data) {
        formik.setFieldValue("video", "");
        formik.setFieldValue("videoName", "");
        setVideo(null);
        setVideoURL(null);
        setOpenDeleteSuccessPopup(true);
        getUser();  
      } else {
        setOpenErrorModal(true);
      }
    } catch (error) {
      console.error(t("CHANGE_VIDEO_DELETE_ERROR"), error);
      setOpenErrorModal(true);
    }
  };

  const videoEmbedUrl = getAllowedExternalVideoEmbedUrl(formik.values.link);

  return openErrorModal ? (
    <Modal onClose={() => setOpenErrorModal(false)}>
      <ErrorPopup onClose={() => setOpenErrorModal(false)} />
    </Modal>
  ) : openDeleteSuccessPopup ? (
    <Modal onClose={() => setOpenDeleteSuccessPopup(false)}>
      <ChangePasswordSuccessPopup onClose={onClose} type="delete-video" />
    </Modal>
  ) : openModal ? (
    <ChangePasswordSuccessPopup onClose={onClose} type="video" />
  ) : (
    <article className={changeVideoPopup.popup}>
      <div className={changeVideoPopup.popup__content}>
        <h2 className={changeVideoPopup.popup__title}>
          {t("CHANGE_VIDEO_TITLE")}
        </h2>
        <form
          id="updateVideo"
          onSubmit={formik.handleSubmit}
          className={changeVideoPopup.popup__form}
        >
          <label
            htmlFor={"link"}
            className={changeVideoPopup.popup__link_label}
          >
            {t("CHANGE_VIDEO_LINK_LABEL")}
          </label>
          <input
            type="text"
            name={"link"}
            id={"link"}
            placeholder={t("CHANGE_VIDEO_LINK_PLACEHOLDER")}
            value={formik.values.link}
            onChange={handleLinkChange}
            onBlur={formik.handleBlur}
            className={`${changeVideoPopup.popup__link_input} ${
              formik.errors.link && changeVideoPopup.popup__input_invalid
            }`}
          />

          {/* preview for link */}
          {videoEmbedUrl && (
            <div className={changeVideoPopup.previewWrapper}>
              <iframe
                src={videoEmbedUrl}
                title="video-preview"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              ></iframe>
              <button
                type="button"
                onClick={clearVideoLink}
                className={changeVideoPopup.closeBtn}
              >
                ×
              </button>
            </div>
          )}

          <p>{t("CHANGE_VIDEO_OR")}</p>

          <div className={changeVideoPopup.file_input_wrapper}>
            {formik.values.video && videoURL ? (
              <div className={changeVideoPopup.previewWrapper}>
                <video src={videoURL} controls />
                <button
                  type="button"
                  onClick={clearVideoFile}
                  className={changeVideoPopup.closeBtn}
                >
                  ×
                </button>
              </div>
            ) : (
              <>
            <label htmlFor={"video"} className={changeVideoPopup.popup__label}>
              <img
                src={uploadIcon}
                alt={t("CHANGE_VIDEO_UPLOAD_ICON_ALT")}
                className={changeVideoPopup.popup__img_upload}
              />
              <p>{t("CHANGE_VIDEO_UPLOAD_LABEL")}</p>
            </label>
            <input
              type="file"
              name={"video"}
              id={"video"}
              placeholder={t("CHANGE_VIDEO_LINK_PLACEHOLDER")}
              accept="video/*"
              onChange={(e) => handleVideoChange(e)}
              onBlur={formik.handleBlur}
              className={changeVideoPopup.popup__input_hiden}
            />
            </>
            )}
          </div>

          {(formik.errors.video || formik.errors.link) && (
            <p className={`${loginStyles.login__invalid_message}`}>
              {t(
                formik.errors.video ? formik.errors.video : formik.errors.link
              )}
            </p>
          )}
          {(isUploadError || isLinkError || isDeleteError) && (
            <p className={`${loginStyles.login__invalid_message}`}>
              {updateError}
            </p>
          )}
          <button
            type="submit"
            form="updateVideo"
            disabled={
              formik.isSubmitting ||
              (!formik.isValid || (!video && !formik.values.link))
            }
            className={`button hover ${changeVideoPopup.popup__submitBtn} ${
              formik.isSubmitting ||
              (!formik.isValid || (!video && !formik.values.link))
                ? changeVideoPopup.popup__submitBtn_disabled
                : ""
            }`}
          >
            {t("CHANGE_VIDEO_UPDATE_BUTTON")}
          </button>
          {errorMessage && <p className={changeVideoPopup.error}>{errorMessage}</p>}
          <button
            type="button"
            form="deleteVideo"
            onClick={handleDeleteVideo}
            disabled={!hasVideo}
            className={`button hover ${changeVideoPopup.popup__submitBtn} ${
              !hasVideo ? changeVideoPopup.popup__submitBtn_disabled : ""
            }`}
          >
            {t("CHANGE_VIDEO_DELETE_BUTTON")}
          </button>
        </form>
      </div>
    </article>
  );
}

export default ChangeVideoPopup;
